import {
  ROTULO_DA_ZONA_DE_NPS,
  TAXA_DE_RESPOSTA_MINIMA_CONFIAVEL,
  zonaDoNps,
  type AgregadoDoCriterio,
  type ZonaDeNps,
} from '@locaveri/shared'
import { type ReactElement, useState } from 'react'

import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useRelatorioDaPesquisa } from '@/features/pesquisa/api'

/**
 * RELATÓRIO DE NPS — e a coisa que a referência não mostra.
 *
 * ===========================================================================
 * ⚠️ A TAXA DE RESPOSTA VEM ANTES DO NÚMERO, E NÃO DEPOIS
 * ===========================================================================
 * A tela do concorrente mostra "12 reservas, 0 enquetes, NPS geral: 0" com o
 * mesmo peso visual de um NPS de verdade. Zero é um NPS legítimo — significa
 * tantos promotores quanto detratores. Ausência de dado NÃO é zero, e exibir os
 * dois iguais convida o gestor a decidir sobre ruído.
 *
 * Aqui a taxa é um KPI de primeira classe, e abaixo do piso de confiança
 * (`TAXA_DE_RESPOSTA_MINIMA_CONFIAVEL`, no shared) o agregado sai com ressalva
 * explícita — não escondido, porque esconder também mente, mas marcado.
 *
 * ===========================================================================
 * A MATEMÁTICA NÃO ESTÁ AQUI
 * ===========================================================================
 * NPS, faixas e zonas vêm calculados da API, que usa as mesmas funções do
 * `@locaveri/shared`. Esta tela só escolhe cor e ordem. Recalcular aqui é como
 * o painel e o relatório passariam a discordar sobre o mesmo número.
 *
 * ===========================================================================
 * O RECORTE POR PONTO DE RETIRADA É O QUE VIRA ORDEM DE SERVIÇO
 * ===========================================================================
 * "A limpeza cai" não é acionável. "A limpeza cai no aeroporto" é. Por isso o
 * recorte por local tem a mesma altura da tela que o agregado geral.
 */

const COR_DA_ZONA: Readonly<Record<ZonaDeNps, 'danger' | 'warning' | 'info' | 'success'>> = {
  CRITICA: 'danger',
  MELHORIA: 'warning',
  QUALIDADE: 'info',
  EXCELENCIA: 'success',
}

/** `null` é "sem resposta", e a tela o escreve por extenso — nunca como 0. */
function Nps({ valor }: { readonly valor: number | null }): ReactElement {
  if (valor === null) {
    return <span className="text-fg-muted">sem resposta</span>
  }

  const zona = zonaDoNps(valor)
  return (
    <span className="inline-flex items-center gap-2">
      <span className="font-semibold tabular-nums">{valor}</span>
      <Badge variant={COR_DA_ZONA[zona]}>{ROTULO_DA_ZONA_DE_NPS[zona]}</Badge>
    </span>
  )
}

/**
 * As três faixas, na ordem fixa promotor → passivo → detrator.
 *
 * A ordem é parte da leitura: com ela, "a barra escureceu para a direita" quer
 * dizer sempre a mesma coisa, e a comparação entre duas linhas da tabela não
 * exige ler os números.
 */
const FAIXAS_DA_BARRA = [
  { chave: 'promotores', rotulo: 'Promotores', classe: 'bg-success' },
  { chave: 'passivos', rotulo: 'Passivos', classe: 'bg-fg-muted' },
  { chave: 'detratores', rotulo: 'Detratores', classe: 'bg-danger' },
] as const

/**
 * ⚠️ A LEGENDA DA BARRA — ela não é enfeite.
 *
 * O CLAUDE.md proíbe status que dependa só de cor, e a barra depende: verde,
 * cinza e vermelho, sendo que verde/vermelho é justamente o par que a
 * daltonia mais comum não separa. O `aria-label` de cada barra resolve para
 * leitor de tela e NÃO resolve para quem enxerga — e é essa pessoa que está
 * olhando a tabela.
 *
 * A legenda aparece UMA vez, acima da tabela, e não em cada linha: repetida
 * trinta vezes ela viraria ruído e ninguém a leria nenhuma.
 */
function LegendaDaBarra(): ReactElement {
  return (
    <ul className="flex flex-wrap gap-4 text-xs text-fg-secondary">
      {FAIXAS_DA_BARRA.map((faixa) => (
        <li key={faixa.chave} className="flex items-center gap-2">
          <span className={`size-2 rounded-full ${faixa.classe}`} aria-hidden />
          {faixa.rotulo}
        </li>
      ))}
    </ul>
  )
}

/**
 * A distribuição de um critério, como barra.
 *
 * Promotor, passivo e detrator na mesma linha e em proporção: é o que deixa "o
 * NPS caiu porque os promotores viraram passivos" ser visto sem ler três
 * números. Sem resposta nenhuma, a barra não é desenhada — uma barra vazia
 * pareceria "tudo detrator".
 *
 * Cada segmento leva `title` além do `aria-label` do conjunto: o primeiro
 * atende o mouse, o segundo o leitor de tela, e a legenda acima atende quem só
 * olha. Os três públicos, e nenhum deles depende da cor sozinha.
 */
function Distribuicao({ criterio }: { readonly criterio: AgregadoDoCriterio }): ReactElement {
  if (criterio.respostas === 0) {
    return <span className="text-fg-muted">—</span>
  }

  const partes = FAIXAS_DA_BARRA.map((faixa) => ({
    ...faixa,
    valor: criterio[faixa.chave],
  }))

  return (
    <div
      className="flex h-2 w-full min-w-32 overflow-hidden rounded-full bg-elevated"
      role="img"
      aria-label={partes.map((parte) => `${parte.rotulo}: ${String(parte.valor)}`).join(', ')}
    >
      {partes.map((parte) => (
        <div
          key={parte.chave}
          className={parte.classe}
          title={`${parte.rotulo}: ${String(parte.valor)}`}
          style={{ width: `${String((parte.valor / criterio.respostas) * 100)}%` }}
        />
      ))}
    </div>
  )
}

/** `AAAA-MM-DD` de hoje menos `dias`. */
function diasAtras(dias: number): string {
  const data = new Date()
  data.setDate(data.getDate() - dias)
  return data.toISOString().slice(0, 10)
}

export function PesquisaRelatorioPage(): ReactElement {
  const [de, setDe] = useState(() => diasAtras(90))
  const [ate, setAte] = useState(() => diasAtras(0))

  const consulta = useRelatorioDaPesquisa({ de, ate })
  const relatorio = consulta.data

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 p-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Satisfação e NPS</h1>
        <p className="max-w-prose text-sm text-fg-secondary">
          O que os clientes responderam depois de devolver o carro, por critério e por ponto de
          retirada.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-2">
          <Label htmlFor="periodo-de">De</Label>
          <Input
            id="periodo-de"
            type="date"
            value={de}
            onChange={(evento) => {
              setDe(evento.target.value)
            }}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label htmlFor="periodo-ate">Até</Label>
          <Input
            id="periodo-ate"
            type="date"
            value={ate}
            onChange={(evento) => {
              setAte(evento.target.value)
            }}
          />
        </div>
      </div>

      {consulta.isPending && <LoadingState rotulo="Carregando o relatório…" linhas={4} />}

      {consulta.isError && (
        <ErrorState
          titulo="Não foi possível carregar o relatório"
          erro={consulta.error}
          aoTentarNovamente={() => {
            void consulta.refetch()
          }}
        />
      )}

      {relatorio !== undefined && (
        <>
          {/* --------------------------------------------------------------
              ⚠️ A ressalva vem ANTES dos números
              -------------------------------------------------------------- */}
          {!relatorio.confiavel && (
            <Alert variant="warning">
              <AlertTitle>Poucas respostas para concluir alguma coisa</AlertTitle>
              <AlertDescription>
                {relatorio.pesquisasEnviadas === 0
                  ? 'Nenhuma pesquisa foi enviada neste período. O envio automático precisa estar ligado na configuração.'
                  : `Só ${String(relatorio.pesquisasRespondidas)} de ${String(relatorio.pesquisasEnviadas)} pesquisas voltaram (${String(relatorio.taxaDeResposta ?? 0)}%). Abaixo de ${String(TAXA_DE_RESPOSTA_MINIMA_CONFIAVEL)}% o NPS abaixo é indicativo, não conclusão.`}
              </AlertDescription>
            </Alert>
          )}

          {/* --------------------------------------------------------------
              Os quatro números do topo
              -------------------------------------------------------------- */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>NPS geral</CardDescription>
              </CardHeader>
              <CardContent className="text-2xl">
                <Nps valor={relatorio.npsGeral} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Taxa de resposta</CardDescription>
              </CardHeader>
              <CardContent className="text-2xl font-semibold tabular-nums">
                {relatorio.taxaDeResposta === null ? (
                  <span className="text-base font-normal text-fg-muted">sem envio</span>
                ) : (
                  `${String(relatorio.taxaDeResposta)}%`
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Pesquisas enviadas</CardDescription>
              </CardHeader>
              <CardContent className="text-2xl font-semibold tabular-nums">
                {relatorio.pesquisasEnviadas}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Respondidas</CardDescription>
              </CardHeader>
              <CardContent className="text-2xl font-semibold tabular-nums">
                {relatorio.pesquisasRespondidas}
              </CardContent>
            </Card>
          </div>

          {/* --------------------------------------------------------------
              Por critério
              -------------------------------------------------------------- */}
          <Card>
            <CardHeader>
              <CardTitle>Por critério</CardTitle>
              <CardDescription>
                O rótulo é o que estava valendo quando a pessoa respondeu. Renomear um critério faz
                os dois nomes conviverem aqui — e é assim que a série histórica fica inteira.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <LegendaDaBarra />

              <Table>
                <TableCaption>
                  {relatorio.porCriterio.length === 0
                    ? 'Nenhuma resposta neste período.'
                    : `${String(relatorio.porCriterio.length)} critério(s) com resposta.`}
                </TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead>Critério</TableHead>
                    <TableHead className="text-right">Respostas</TableHead>
                    <TableHead className="text-right">Média</TableHead>
                    <TableHead className="w-40">Distribuição</TableHead>
                    <TableHead>NPS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {relatorio.porCriterio.map((criterio) => (
                    <TableRow key={criterio.criterioId}>
                      <TableCell className="font-medium">{criterio.rotulo}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {criterio.respostas}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {criterio.media ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Distribuicao criterio={criterio} />
                      </TableCell>
                      <TableCell>
                        <Nps valor={criterio.nps} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* --------------------------------------------------------------
              Por ponto de retirada — o recorte que vira ordem de serviço
              -------------------------------------------------------------- */}
          <Card>
            <CardHeader>
              <CardTitle>Por ponto de retirada</CardTitle>
              <CardDescription>
                É o corte acionável: “a limpeza cai” não é ordem de serviço; “a limpeza cai no
                aeroporto” é.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableCaption>
                  {relatorio.porLocal.length === 0
                    ? 'Nenhuma resposta neste período.'
                    : `${String(relatorio.porLocal.length)} ponto(s) com resposta.`}
                </TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ponto</TableHead>
                    <TableHead className="text-right">Respostas</TableHead>
                    <TableHead>NPS</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {relatorio.porLocal.map((local) => (
                    <TableRow key={local.localId ?? 'sem-local'}>
                      <TableCell className="font-medium">
                        {local.local ?? 'Sem ponto informado'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{local.respostas}</TableCell>
                      <TableCell>
                        <Nps valor={local.nps} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
